// WaterfallChartFinal.jsx
import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell, LabelList
} from "recharts";
import { Card, Box, Typography } from "@mui/material";

// Auto generator (use raw input)
export function generateWaterfallData(rawItems) {
    const colors = { positive: "#56BC4C", negative: "#E74C3C" };

    let subtotal = 0;
    let isFirst = true; // very important!

    return rawItems.map((item) => {
        const value = item.value;

        // -----------------------------
        // POSITIVE BAR (subtotals)
        // -----------------------------
        if (value >= 0) {
            if (isFirst) {
                // First bar sets starting subtotal
                subtotal = value;
                isFirst = false;
            }

            return {
                label: item.label,
                value,
                start: 0,
                end: value,
                barHeight: value,
                labelValue: value,
                baseline: value,       // top label only
                fill: colors.positive,
                isNegative: false
            };
        }

        // -----------------------------
        // NEGATIVE BAR (floating bars)
        // -----------------------------
        const start = subtotal;
        subtotal = subtotal + value; // value is negative
        const end = subtotal;

        return {
            label: item.label,
            value,
            start,
            end,
            barHeight: Math.abs(value),
            labelValue: Math.abs(value),
            baseline: start,         // *** CORRECT BASELINE ***
            fill: colors.negative,
            isNegative: true
        };
    });
}




function TopLabelRenderer(props) {
    const { x, y, width, index } = props;

    const row = props.chartData[index];   // <-- ACCESS FULL ORIGINAL DATA

    if (!row || row.isNegative) return null;

    return (
        <text
            x={x + width / 2}
            y={y - 6}
            textAnchor="middle"
            style={{ fontSize: 10, fill: "#11111180" }}
        >
            ${row.labelValue}M
        </text>
    );
}

function BaselineLabelRenderer(props) {
    const { x, y, width, height, index } = props;

    const row = props.chartData[index];
    if (!row || !row.isNegative) return null;

    return (
        <text
            x={x + width / 2}
            y={y + height + 14}
            textAnchor="middle"
            style={{ fontSize: 10, fill: "#11111180" ,fontWeight:400}}
        >
            ${row.offset}M
        </text>
    );
}

export default function WaterfallChart({ title, rawData }) {

    const data = generateWaterfallData(rawData || []);
    const chartData = data.map((d) => ({
        ...d,
        offset: Math.min(d.start, d.end),
        barHeight: Math.abs(d.end - d.start)
    }));

    const chartWidth = Math.max(chartData.length * 110, 900);

    return (
        <Card
            sx={{
                width: "100%",
                borderRadius: "12px !important",
                background: "#fff",
                 boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "visible"
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    background: "#115f9c",
                    height: "36px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow:"0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            {/* CHART */}
            <Box sx={{
                width: "100%",
                height: 420,
                p: 2,
                overflowX: "auto",
                overflowY: "hidden",
            }}>
                <Box
                    sx={{
                        width: {
                            xs: chartWidth,   //  enable horizontal scroll on mobile
                            sm: chartWidth,   // enable horizontal scroll on tablet
                            md: chartWidth,
                            lg: "100%"
                        },
                        minWidth: {
                            xs: chartWidth,
                            sm: chartWidth,
                            md: chartWidth,
                            lg: "100%"
                        },
                        height: "100%",
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 55, right: 40, left: 20, bottom: 10 }}>
                            <CartesianGrid stroke="#1111110D" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#111111" ,fontWeight:200}} tickLine={false} axisLine={false} />
                            <YAxis
                                tick={{ fontSize: 10, fill: "#11111180", dx: -12,fontWeight:200 }}
                                axisLine={false}
                                tickLine={false}
                                domain={[0, 1000]}
                                ticks={[0, 200, 400, 600, 800, 1000]}
                                label={{
                                    value: "Dollars ($M)",
                                    angle: -90,
                                    position: "insideLeft",
                                    dx: 5,
                                    dy: 40,
                                    style: { fontSize: 10, fill: "#111111",fontWeight:200 }
                                }}
                            />
                            <Tooltip
                                formatter={(value, name, props) => {
                                    const p = props?.payload;
                                    if (!p) return null;

                                    if (p.isNegative) {
                                        return [`$${p.offset}M`, p.label];
                                    }

                                    return [`$${p.labelValue}M`, p.label];
                                }}
                                labelFormatter={(label) => label}
                            />


                            {/* offset bar must not render rectangle, but we still need stackId to position visible bar.
                Use shape={() => null} to prevent the offset rect being drawn. */}
                            <Bar
                                dataKey="offset"
                                stackId="a"
                                shape={() => null}
                                tooltipType="none"
                            >

                            </Bar>

                            {/* Visible bar */}
                            <Bar dataKey="barHeight" stackId="a" isAnimationActive={false} stroke="none">
                                {chartData.map((entry, idx) => (
                                    <Cell key={idx} fill={entry.fill} />
                                ))}

                                <LabelList
                                    dataKey="barHeight"
                                    content={(p) => <TopLabelRenderer {...p} chartData={chartData} />}
                                />
                                <LabelList
                                    dataKey="offset"
                                    content={(p) => <BaselineLabelRenderer {...p} chartData={chartData} />}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    m: '16px'
                }}
            >
                {[
                    { label: "Positive Value", color: "#56BC4C" },
                    { label: "Negative Value", color: "#E74C3C" }
                ].map((s) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: s.color
                            }}
                        />
                        <Typography fontSize={10} style={{color:"#111111",fontWeight:200}}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>

        </Card>
    );
}

